import { useCallback, useRef, useEffect } from "react"
import { useReactFlow } from "@xyflow/react"
import type { CursorState } from "./types"

interface UseCursorCaptureOptions {
	connectionId: string | null
	userId: string
	projectId: string
	onCursorUpdate: (cursor: CursorState) => void
	enabled: boolean
	throttleMs?: number // Default: ~16ms for 60Hz
}

export function useCursorCapture({
	connectionId,
	userId,
	projectId,
	onCursorUpdate,
	enabled,
	throttleMs = 16,
}: UseCursorCaptureOptions) {
	const { getViewport, screenToFlowPosition } = useReactFlow()

	const sequenceNumberRef = useRef(0)
	const lastUpdateRef = useRef(0)
	const lastMousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
	const currentStateRef = useRef<
		"idle" | "selecting" | "dragging" | "panning" | "connecting"
	>("idle")
	const draggedNodesRef = useRef<
		Array<{ id: string; position: { x: number; y: number } }>
	>([])
	const connectingFromRef = useRef<{
		nodeId: string
		handleId: string | null
		handleType: "source" | "target"
	} | null>(null)
	const selectionBoxRef = useRef<{
		x: number
		y: number
		width: number
		height: number
		mode: "partial" | "full"
		selectedNodeIds?: string[]
	} | null>(null)

	const buildCursorState = useCallback(
		(screenX: number, screenY: number): CursorState | null => {
			// Can't build cursor state without connectionId
			if (!connectionId) return null

			const viewport = getViewport()
			const flowPosition = screenToFlowPosition({ x: screenX, y: screenY })

			const cursor: CursorState = {
				connectionId,
				userId,
				projectId,
				position: flowPosition,
				viewport: {
					x: viewport.x,
					y: viewport.y,
					zoom: viewport.zoom,
				},
				state: currentStateRef.current,
				timestamp: Date.now(),
				sequenceNumber: ++sequenceNumberRef.current,
			}

			if (draggedNodesRef.current.length > 0) {
				cursor.draggedNodes = draggedNodesRef.current
			}

			if (connectingFromRef.current) {
				cursor.connectingFrom = connectingFromRef.current
			}

			if (selectionBoxRef.current) {
				cursor.selectionBox = selectionBoxRef.current
			}

			return cursor
		},
		[connectionId, userId, projectId, getViewport, screenToFlowPosition],
	)

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			if (!enabled) return

			// Always track mouse position for immediate state updates
			lastMousePositionRef.current = { x: event.clientX, y: event.clientY }

			const now = Date.now()
			if (now - lastUpdateRef.current < throttleMs) return
			lastUpdateRef.current = now

			const cursor = buildCursorState(event.clientX, event.clientY)
			if (cursor) {
				onCursorUpdate(cursor)
			}
		},
		[enabled, throttleMs, buildCursorState, onCursorUpdate],
	)

	// Send an immediate cursor update (bypasses throttle)
	const sendImmediateUpdate = useCallback(() => {
		if (!enabled) return
		const { x, y } = lastMousePositionRef.current
		const cursor = buildCursorState(x, y)
		if (cursor) {
			onCursorUpdate(cursor)
		}
	}, [enabled, buildCursorState, onCursorUpdate])

	const setDragging = useCallback(
		(
			nodes: Array<{ id: string; position: { x: number; y: number } }> | null,
		) => {
			if (nodes && nodes.length > 0) {
				currentStateRef.current = "dragging"
				draggedNodesRef.current = nodes
			} else {
				currentStateRef.current = "idle"
				draggedNodesRef.current = []
			}
			// Immediately send update when state changes
			sendImmediateUpdate()
		},
		[sendImmediateUpdate],
	)

	const setSelecting = useCallback(
		(
			isSelecting: boolean,
			selectionBox?: {
				x: number
				y: number
				width: number
				height: number
				mode: "partial" | "full"
				selectedNodeIds?: string[]
			},
		) => {
			currentStateRef.current = isSelecting ? "selecting" : "idle"
			selectionBoxRef.current = selectionBox ?? null
			// Only send immediate update on state change (start/end), not during drag
			if (!isSelecting || !selectionBox) {
				sendImmediateUpdate()
			}
		},
		[sendImmediateUpdate],
	)

	const updateSelectionBox = useCallback(
		(selectionBox: {
			x: number
			y: number
			width: number
			height: number
			mode: "partial" | "full"
			selectedNodeIds?: string[]
		}) => {
			selectionBoxRef.current = selectionBox
			// Don't call sendImmediateUpdate - let the throttled mouse move handle it
		},
		[],
	)

	const updateDragPositions = useCallback(
		(nodes: Array<{ id: string; position: { x: number; y: number } }>) => {
			// Only update the ref if we're currently in dragging state
			if (currentStateRef.current === "dragging" && nodes.length > 0) {
				draggedNodesRef.current = nodes
				// Send updates immediately since React Flow drag doesn't trigger mouse move
				sendImmediateUpdate()
			}
		},
		[sendImmediateUpdate],
	)

	const setPanning = useCallback(
		(isPanning: boolean) => {
			currentStateRef.current = isPanning ? "panning" : "idle"
			// Immediately send update when state changes
			sendImmediateUpdate()
		},
		[sendImmediateUpdate],
	)

	const setConnecting = useCallback(
		(
			connecting: {
				nodeId: string
				handleId: string | null
				handleType: "source" | "target"
			} | null,
		) => {
			if (connecting) {
				currentStateRef.current = "connecting"
				connectingFromRef.current = connecting
			} else {
				currentStateRef.current = "idle"
				connectingFromRef.current = null
			}
			// Immediately send update when state changes
			sendImmediateUpdate()
		},
		[sendImmediateUpdate],
	)

	// Add global mouse move listener
	useEffect(() => {
		if (!enabled) return

		window.addEventListener("mousemove", handleMouseMove, { passive: true })

		return () => {
			window.removeEventListener("mousemove", handleMouseMove)
		}
	}, [enabled, handleMouseMove])

	return {
		setDragging,
		updateDragPositions,
		setSelecting,
		updateSelectionBox,
		setPanning,
		setConnecting,
		getCurrentState: () => currentStateRef.current,
		lastMousePositionRef,
	}
}
